/** @jsx jsx */
import { jsx } from "frontend/src/utils/jsx"
import { SectionGrid, Grid } from "frontend/src/components/Layout"
import { BlockText } from "frontend/src/components/BlockText"
import { CMSLink } from "frontend/src/components/CMSLink"
import React from "react"

function ImageOrVideo({ url, title, style }) {
  if (url.match(/\.(jpeg|jpg|gif|png)$/) !== null) {
    return <img src={url} alt={title} loading="lazy" sx={{ ...style }} />
  }
  return (
    <video autoPlay loop muted playsInline sx={{ ...style }}>
      <source src={url} type="video/mp4" />
    </video>
  )
}

function StepCardDesktop({ step, index }) {
  const { title, imageInput, description } = step
  const imageUrl =
    "fileInput" in imageInput
      ? imageInput.fileInput.asset.url
      : "fileUrl" in imageInput
      ? imageInput.fileUrl
      : null
  return (
    <div sx={{ width: "100%", display: ["none", "none", "block"] }}>
      {imageUrl ? (
        <ImageOrVideo
          url={imageUrl}
          title={title}
          style={{
            width: ["24.75vw", "99px", "8.229375vw", "99px"],
            height: ["24.75vw", "99px", "8.229375vw", "99px"],
            borderRadius: ["12.375vw", "49.5px", "4.1146875vw", "49.5px"],
            marginX: "auto",
            display: "block",
            marginBottom: 5,
          }}
        />
      ) : (
        <div
          sx={{
            width: ["24.75vw", "99px", "8.229375vw", "99px"],
            height: ["24.75vw", "99px", "8.229375vw", "99px"],
            marginX: "auto",
            display: "block",
            marginBottom: 5,
          }}
        >
          &nbsp;
        </div>
      )}
      <h3
        sx={{
          fontFamily: "body",
          fontSize: ["4.4584vw", "17.8336px", "1.482418vw", "17.8336px"],
          fontWeight: 500,
          lineHeight: ["5.5vw", "22px", "1.82875vw", "22px"],
          my: 3,
          textAlign: "center",
          color: "black",
        }}
      >
        {title}
      </h3>
      <div sx={{ width: "80%", marginX: "auto" }}>
        <p
          sx={{
            fontFamily: "body",
            textAlign: "center",
            fontSize: ["3.9628vw", "15.8512px", "1.317631vw", "15.8512px"],
            lineHeight: ["6.5vw", "26px", "2.16125vw", "26px"],
            color: "black",
            fontWeight: 400,
          }}
        >
          {description}
        </p>
      </div>
    </div>
  )
}

function StepCardMobile({ step, index }) {
  const { title, imageInput, description } = step
  const imageUrl =
    "fileInput" in imageInput
      ? imageInput.fileInput.asset.url
      : "fileUrl" in imageInput
      ? imageInput.fileUrl
      : null
  const mt = index === 0 ? "none" : 6
  return (
    <div
      sx={{
        width: "100%",
        display: ["grid", "grid", "none"],
        gridTemplateColumns: "1fr 2fr",
        gridColumnGap: "20px",
        gridRowGap: "50px",
      }}
    >
      <div sx={{ gridColumn: "1 / span 1" }}>
        {imageUrl ? (
          <ImageOrVideo
            url={imageUrl}
            alt={title}
            style={{
              width: ["24.75vw", "99px", "8.229375vw", "99px"],
              height: ["24.75vw", "99px", "8.229375vw", "99px"],
              borderRadius: ["12.375vw", "49.5px", "4.1146875vw", "49.5px"],
              marginX: "auto",
              display: "block",
              mt: mt,
              mb: 5,
            }}
          />
        ) : (
          <div
            sx={{
              width: ["24.75vw", "99px", "8.229375vw", "99px"],
              height: ["24.75vw", "99px", "8.229375vw", "99px"],
              marginX: "auto",
              display: "block",
              mt: mt,
              mb: 5,
            }}
          >
            &nbsp;
          </div>
        )}
      </div>
      <div
        sx={{
          gridColumn: "2 / span 1",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-around",
          alignItems: "flex-start",
          mt: mt,
        }}
      >
        <div sx={{ width: "80%", mr: 4 }}>
          <h3
            sx={{
              fontFamily: "body",
              fontWeight: 500,
              fontSize: ["4.4584vw", "17.8336px", "1.482418vw", "17.8336px"],
              lineHeight: ["5.5vw", "22px", "1.82875vw", "22px"],
              my: 3,
              color: "black",
            }}
          >
            {title}
          </h3>
          <p
            sx={{
              fontFamily: "body",
              fontWeight: 400,
              fontSize: ["3.9628vw", "15.8512px", "1.317631vw", "15.8512px"],
              lineHeight: ["6.5vw", "26px", "2.16125vw", "26px"],
              color: "black",
            }}
          >
            {description}
          </p>
        </div>
      </div>
    </div>
  )
}

function PromisesButton({ button }) {
  const { buttonDesign, buttonText, buttonUrl } = button
  if (
    buttonDesign === "rectangular" &&
    buttonText &&
    buttonUrl &&
    buttonText !== "" &&
    buttonUrl !== ""
  ) {
    return (
      <div sx={{ display: "flex", flexDirection: "row", justifyContent: "center", mt: 8 }}>
        <CMSLink
          link={{
            externalUrl: buttonUrl,
            newtab: false,
            follow: false,
          }}
          sx={{
            textDecoration: "none",
            position: "relative",
            opacity: 1,
            transition: "all 0.1s linear",
            py: [4, 4, 4, 5],
            px: [7, 7, 7, 12],
            textAlign: "center",
            backgroundColor: "almostBlack",
            color: "background",
            fontFamily: "heading",
            textTransform: "uppercase",
            fontSize: 2,
            fontWeight: 300,
            lineHeight: 1.5,
            outline: "none",
            "&:active": {
              outline: "none",
            },
          }}
        >
          {buttonText}
        </CMSLink>
      </div>
    )
  } else if (
    buttonDesign === "rounded" &&
    buttonText &&
    buttonUrl &&
    buttonText !== "" &&
    buttonUrl !== ""
  ) {
    return (
      <div sx={{ display: "flex", flexDirection: "row", justifyContent: "center", mt: 8 }}>
        <CMSLink
          link={{
            externalUrl: buttonUrl,
            newtab: false,
            follow: false,
          }}
          sx={{
            textAlign: "center",
            backgroundColor: "almostBlack",
            color: "background",
            fontFamily: "body",
            fontWeight: 700,
            borderRadius: "41px",
            outline: "none",
            width: ["68.75vw", "275px", "22.859375vw", "275px"],
            height: ["12.5vw", "50px", "4.15625vw", "50px"],
            lineHeight: ["12.5vw", "50px", "4.15625vw", "50px"],
            fontSize: ["6.192vw", "24.768px", "2.05884vw", "24.768px"],
            "&:active": {
              outline: "none",
            },
          }}
        >
          {buttonText}
        </CMSLink>
      </div>
    )
  }
  return null
}

function PromisesWithImage({ title, text, steps, button }) {
  return (
    <SectionGrid sx={{ py: 10, backgroundColor: "background" }}>
      <SectionGrid.narrow>
        <div sx={{ width: ["90vw", "360px", "60.598125vw", "729px"], mx: "auto" }}>
          <h2
            sx={{
              marginX: "auto",
              textAlign: "center",
              color: "almostBlack",
              fontFamily: "body",
              fontWeight: "700",
              fontSize: ["5.6964vw", "22.7856px", "2.470608vw", "29.7216px"],
              lineHeight: ["5.75vw", "23px", "2.9925vw", "36px"],
            }}
          >
            {title}
          </h2>
          <BlockText blocks={text} />
          <Grid columns={["1fr", "1fr", "1fr 1fr 1fr"]} sx={{ mt: 9 }}>
            {steps &&
              steps.map((step, index) => {
                return (
                  <React.Fragment>
                    <StepCardDesktop step={step} index={index} key={index} />
                    <StepCardMobile step={step} index={index} key={index} />
                  </React.Fragment>
                )
              })}
          </Grid>
        </div>
        {"showButton" in button && button.showButton && <PromisesButton button={button} />}
      </SectionGrid.narrow>
    </SectionGrid>
  )
}

export { PromisesWithImage }
